import type { MemberManagementData } from '../member/model';
import { normalizeRequiredText } from '../member/model';
import type { ResourceFile, ResourceFilePlacement, ResourceFolder, ResourceFolderPlacement, Song } from './model';

interface ResourceRulePorts {
  readonly now: () => string;
  readonly newId: (prefix: string) => string;
}

interface CompletedResourceUpload {
  readonly fileUploadId: string;
  readonly objectKey: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly extension: string;
  readonly sizeBytes: number;
}

type AttachResourceFilePlacement = Exclude<ResourceFilePlacement, 'song_current_score'>;

// Returned for immediate notification creation; update notes are not persisted on ResourceFile.
interface CurrentScoreReplacement extends ResourceFile {
  readonly updateNote: string | null;
}

export function createSong(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly actorSeasonMembershipId: string;
    readonly name: string;
    readonly ports: ResourceRulePorts;
  }
): { readonly data: MemberManagementData; readonly song: Song } {
  const song: Song = {
    songId: input.ports.newId('song'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    name: normalizeRequiredText(input.name, 'Song name'),
    currentScoreResourceFileId: null,
    createdBySeasonMembershipId: input.actorSeasonMembershipId,
    createdAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      songs: [...data.songs, song]
    },
    song
  };
}

export function createResourceFolder(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly actorSeasonMembershipId: string;
    readonly placement: ResourceFolderPlacement | 'song_current_score';
    readonly songId: string | null;
    readonly parentFolderId: string | null;
    readonly name: string;
    readonly ports: ResourceRulePorts;
  }
): { readonly data: MemberManagementData; readonly folder: ResourceFolder } {
  if (input.placement === 'song_current_score') {
    throw new Error('Current score cannot contain folders');
  }

  const folder: ResourceFolder = {
    resourceFolderId: input.ports.newId('resourceFolder'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    placement: input.placement,
    songId: input.songId,
    parentFolderId: input.parentFolderId,
    name: normalizeRequiredText(input.name, 'Folder name'),
    createdBySeasonMembershipId: input.actorSeasonMembershipId,
    createdAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      resourceFolders: [...data.resourceFolders, folder]
    },
    folder
  };
}

export function attachResourceFile(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly actorSeasonMembershipId: string;
    readonly placement: AttachResourceFilePlacement;
    readonly songId: string | null;
    readonly parentFolderId: string | null;
    readonly upload: CompletedResourceUpload;
    readonly ports: ResourceRulePorts;
  }
): { readonly data: MemberManagementData; readonly file: ResourceFile } {
  if ((input.placement as ResourceFilePlacement) === 'song_current_score') {
    throw new Error('Use replaceSongCurrentScore for current scores');
  }

  const file = buildResourceFile(input);

  return {
    data: {
      ...data,
      resourceFiles: [...data.resourceFiles, file]
    },
    file
  };
}

export function replaceSongCurrentScore(
  data: MemberManagementData,
  input: {
    readonly songId: string;
    readonly actorSeasonMembershipId: string;
    readonly upload: CompletedResourceUpload;
    readonly updateNote: string;
    readonly ports: ResourceRulePorts;
  }
): {
  readonly data: MemberManagementData;
  readonly song: Song;
  readonly currentScore: CurrentScoreReplacement;
  readonly oldObjectKey: string | null;
} {
  const song = data.songs.find((candidate) => candidate.songId === input.songId);
  if (!song) {
    throw new Error('Song not found');
  }

  const oldCurrentScore = findCurrentScore(data, song);
  const currentScoreFile = buildResourceFile({
    choirId: song.choirId,
    seasonId: song.seasonId,
    actorSeasonMembershipId: input.actorSeasonMembershipId,
    placement: 'song_current_score',
    songId: song.songId,
    parentFolderId: null,
    upload: input.upload,
    ports: input.ports
  });
  const currentScore: CurrentScoreReplacement = {
    ...currentScoreFile,
    updateNote: input.updateNote.trim() === '' ? null : input.updateNote.trim()
  };
  const updatedSong: Song = {
    ...song,
    currentScoreResourceFileId: currentScore.resourceFileId
  };

  return {
    data: {
      ...data,
      songs: data.songs.map((candidate) => (candidate.songId === updatedSong.songId ? updatedSong : candidate)),
      resourceFiles: [
        ...data.resourceFiles.filter((file) => file.resourceFileId !== song.currentScoreResourceFileId),
        currentScoreFile
      ]
    },
    song: updatedSong,
    currentScore,
    oldObjectKey: oldCurrentScore?.objectKey ?? null
  };
}

export function deleteSongIfEmpty(
  data: MemberManagementData,
  input: { readonly songId: string }
): { readonly data: MemberManagementData } {
  const song = data.songs.find((candidate) => candidate.songId === input.songId);
  if (!song) {
    throw new Error('Song not found');
  }

  const hasResourceFiles = data.resourceFiles.some((file) => file.songId === song.songId);
  const hasResourceFolders = data.resourceFolders.some((folder) => folder.songId === song.songId);
  if (song.currentScoreResourceFileId !== null || hasResourceFiles || hasResourceFolders) {
    throw new Error('Song must be empty before deletion');
  }

  return {
    data: {
      ...data,
      songs: data.songs.filter((candidate) => candidate.songId !== song.songId)
    }
  };
}

function findCurrentScore(data: MemberManagementData, song: Song): ResourceFile | null {
  if (song.currentScoreResourceFileId === null) {
    return null;
  }

  const currentScore = data.resourceFiles.find((file) => file.resourceFileId === song.currentScoreResourceFileId);
  if (!currentScore) {
    throw new Error('Current score file not found');
  }

  return currentScore;
}

function buildResourceFile(input: {
  readonly choirId: string;
  readonly seasonId: string;
  readonly actorSeasonMembershipId: string;
  readonly placement: ResourceFilePlacement;
  readonly songId: string | null;
  readonly parentFolderId: string | null;
  readonly upload: CompletedResourceUpload;
  readonly ports: ResourceRulePorts;
}): ResourceFile {
  return {
    resourceFileId: input.ports.newId('resourceFile'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    placement: input.placement,
    songId: input.songId,
    parentFolderId: input.placement === 'song_current_score' ? null : input.parentFolderId,
    fileUploadId: input.upload.fileUploadId,
    objectKey: input.upload.objectKey,
    fileName: input.upload.fileName,
    mimeType: input.upload.mimeType,
    extension: input.upload.extension,
    sizeBytes: input.upload.sizeBytes,
    createdBySeasonMembershipId: input.actorSeasonMembershipId,
    createdAt: input.ports.now()
  };
}
